"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Filter, PanelRightClose, PanelRightOpen } from "lucide-react";
import { useSession } from "next-auth/react";
import { useParams, usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { TiArrowUnsorted } from "react-icons/ti";
import { useOnborda } from "onborda";
// import { GrProjects } from "react-icons/gr";
import { LuChartGantt, LuPlus } from "react-icons/lu";
import { MdOutlineAdminPanelSettings } from "react-icons/md";
import { TbHexagonPlusFilled, TbTemplate } from "react-icons/tb";

import { getSubscriptionByOrgId } from "@/actions/subscriptions";
import { getMasterWorkByOrgId } from "@/actions/work";
import { DashboardNav } from "@/components/dashboard/dashboard-nav";
import Logo from "@/components/icons/logo";
import SharedSidebar from "@/components/shared-sidebar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TreeDropdown } from "@/components/ui/tree-dropdown";
import { useChatSwitch } from "@/hooks/use-chat-switch";
import useInvite from "@/hooks/use-invite";
import { treeNodesToTreeItems, useOrgTree } from "@/hooks/use-org-tree";
import { useProjectNavItems } from "@/hooks/use-work-items";
import { useWorkAccessLevels } from "@/hooks/useWorkAccessLevels";
import { subscribeCollapseAppSidebar, subscribeExpandAppSidebar } from "@/lib/chat/chat-layout-persistence";
import { cn, isValidOrgId } from "@/lib/utils";

import { SharedSidebarFooter } from "../shared-sidebar";
import { CreateWorkForm } from "./forms/create-work-form";
import { ProductSelector } from "@/components/product/product-selector";
import { useProductFilter } from "@/hooks/use-product-filter";
import { useProductsByOrgId } from "@/hooks/use-products-by-org-id";
import { useFunctionFacetIdsForProduct } from "@/hooks/use-function-facet-ids-for-product";
import { groupSidebarProjectNavItems } from "@/lib/product-facet/sidebar-project-nav";
import { UserNav } from "./user-nav";

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {}

export default function Sidebar({ className }: Readonly<SidebarProps>) {
  const params = useParams();
  const orgId = params?.orgId as string;
  const projectId = params?.projectId as string;
  const router = useRouter();
  const session = useSession();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { selectedView } = useChatSwitch();
  const { setInviteModalOpen } = useInvite();
  const [isCreateWorkFormOpen, setIsCreateWorkFormOpen] = useState(false);

  const validOrgId = isValidOrgId(orgId) ? orgId : undefined;

  const { isLoading, isError, fetchWorkItems } = useProjectNavItems();
  const { products, isLoading: isProductsLoading } = useProductsByOrgId();
  const { productFilter } = useProductFilter();
  const preferredProductRootId =
    typeof productFilter === "object" ? productFilter.productId : null;
  const productRootId =
    typeof productFilter === "object" ? productFilter.productId : undefined;
  const { functionFacetIds } = useFunctionFacetIdsForProduct(productRootId);
  const { workAccessLevels, isLoading: isWorkAccessLevelsLoading } =
    useWorkAccessLevels();
  const [selectedAccessLevelFilter, setSelectedAccessLevelFilter] = useState<
    string | null
  >(null);

  // Fetch work items to filter by access level
  // Throw 403 errors to trigger error boundary - critical for route-level authorization
  const { data: workItems } = useQuery({
    queryKey: ["masterWorkItems", validOrgId],
    queryFn: () => getMasterWorkByOrgId(validOrgId!),
    enabled: !!validOrgId,
    retry: (failureCount, error) => {
      // Don't retry on 403 errors
      const errorObj = error as unknown as Record<string, unknown> & {
        status?: number;
      };
      if (errorObj?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
    // Throw 403 errors to trigger error boundary, but not other errors
    throwOnError: (error) => {
      const errorObj = error as unknown as Record<string, unknown> & {
        status?: number;
        name?: string;
      };

      // Only treat real ApiError/HTTP status 403 as route auth failures.
      // Do not match the substring "403" in unrelated messages (e.g. GitHub Forbidden).
      return (
        errorObj?.status === 403 ||
        (errorObj?.name === "ApiError" && errorObj?.status === 403)
      );
    },
  });

  const accessLevelFilteredWorks = useMemo(() => {
    if (!workItems) return [];
    if (selectedAccessLevelFilter === null) return workItems;
    return workItems.filter(
      (item) => item.accessLevelId === selectedAccessLevelFilter,
    );
  }, [workItems, selectedAccessLevelFilter]);

  const { assignedProjectItems, unlinkedProjectItems } = useMemo(
    () =>
      groupSidebarProjectNavItems(
        accessLevelFilteredWorks,
        productFilter,
        functionFacetIds,
      ),
    [accessLevelFilteredWorks, productFilter, functionFacetIds],
  );

  const hasAnyProjectItems =
    assignedProjectItems.length > 0 || unlinkedProjectItems.length > 0;
  const showUnlinkedSection = unlinkedProjectItems.length > 0;

  // Sidebar open state: default collapsed, persisted in localStorage
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const stored = localStorage.getItem("dashboardSidebarOpen");
      return stored ? stored === "true" : false; // default collapsed
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("dashboardSidebarOpen", String(isOpen));
    } catch {
      // ignore write errors (e.g., private mode)
    }
  }, [isOpen]);

  useEffect(() => {
    return subscribeCollapseAppSidebar(() => {
      setIsOpen(false);
    });
  }, []);

  useEffect(() => {
    return subscribeExpandAppSidebar(() => {
      setIsOpen(true);
    });
  }, []);

  // Org tree data for Team selector
  const { treeData, isPending, selectedOrg, goToOrg } = useOrgTree({});
  const memoData = useMemo(() => treeNodesToTreeItems(treeData), [treeData]);

  // Current subscription to show plan name badge when upgrade is suggested
  const { data: orgSubscription } = useQuery({
    queryKey: ["subscription", validOrgId],
    queryFn: () => getSubscriptionByOrgId(validOrgId!),
    enabled: !!validOrgId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
  });
  const currentPlanName = orgSubscription
    ? orgSubscription.planTierModel?.name?.trim() || "Free"
    : null;

  //not used
  // const onSelectOrg = useCallback(
  //   (item: TreeItem) => {
  //     goToOrg(item.id || "");
  //   },
  //   [goToOrg],
  // );

  const { startOnborda } = useOnborda();

  useEffect(() => {
    if (isProductsLoading) {
      return;
    }

    const timer = globalThis.setTimeout(() => {
      const onborda = localStorage.getItem("onborda");
      if (!onborda && products.length > 0) {
        startOnborda("tour1");
      }
    }, 2000);

    return (): void => {
      globalThis.clearTimeout(timer);
    };
  }, [isProductsLoading, products.length, startOnborda]);

  const handleWorkCreated = () => {
    // Refresh the work tree so Gantt reflects the new item
    queryClient.invalidateQueries({ queryKey: ["workTree", projectId] });
    setIsCreateWorkFormOpen(false);
  };

  // Compute the next view to use in navigation: if currently in full-screen chat, switch
  // to split-view on navigation; otherwise preserve the current view.
  const nextView = selectedView === "chat" ? "split-view" : selectedView;

  const projectFilterLabel =
    selectedAccessLevelFilter === null
      ? "All Projects"
      : `${
          workAccessLevels?.find(
            (level) => level.id === selectedAccessLevelFilter,
          )?.name ?? "Filtered"
        } Projects`;

  return (
    <div
      className={cn(
        // Outer container uses page background with blur (restored)
        "relative z-20 h-[calc(100vh)] overflow-hidden bg-background/80 transition-[width] duration-300 ease-in-out",
        className,
        // Avoid vertical padding to prevent overflow/scroll
        isOpen ? "my-0 !w-[240px] py-0" : "!w-[66px] p-2",
      )}
    >
      {/* Inner floating panel with rounded corners and backdrop */}
      <div
        className={cn("h-full bg-foreground/5 px-2 backdrop-blur-sm", {
          "rounded-md": !isOpen,
        })}
      >
        {/* Top header with logo (expanded shows brand text, collapsed shows icon only) */}
        {isOpen ? (
          <div className="-mx-2 relative mb-2 flex items-center justify-start bg-background p-2">
            <Link
              href={`/${orgId}/dashboard?view=${nextView}`}
              className="inline-flex items-center rounded-md hover:opacity-90"
              aria-label="Dashboard"
            >
              <Logo className="h-7 w-auto max-h-8" />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              title="Collapse Sidebar"
              onClick={() => setIsOpen(false)}
              id="close-app-sidebar"
              className="absolute right-2 size-8 rounded-md text-foreground hover:bg-background/90"
            >
              <PanelRightOpen className="size-4" />
            </Button>
          </div>
        ) : (
          <div className="flex w-full justify-start px-0 py-2">
            <Link
              href={`/${orgId}/dashboard?view=${nextView}`}
              className="inline-flex items-center rounded-md hover:opacity-90"
              aria-label="Dashboard"
            >
              <Logo variant="mark" />
            </Link>
          </div>
        )}
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          {isOpen ? (
            <div className="flex h-full min-h-0 flex-col overflow-hidden">
              {/* Team selector (expanded) */}
              {(session.data?.user?.role === "SUPER_ADMIN" ||
                session.data?.user?.role === "ADMIN") && (
                <div className="mb-2 px-0" id="tour1-step1-usesequelizerssistant">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      title={selectedOrg?.name || "Select Organization"}
                      className="size-8 shrink-0 rounded-md border-0 bg-[#922CF8] p-0 text-white hover:bg-[#922CF8]/90"
                      onClick={() =>
                        router.push(`/${orgId}/admin?view=${nextView}`)
                      }
                    >
                      <span className="text-xs font-semibold">
                        {(selectedOrg?.name?.[0] || "T").toUpperCase()}
                      </span>
                    </Button>

                    <div className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-white">
                      <Button
                        variant="ghost"
                        className={cn(
                          "flex min-w-0 flex-1 items-center justify-between rounded-md p-2 text-sm font-medium hover:bg-background/90",
                        )}
                        onClick={() =>
                          router.push(`/${orgId}/admin?view=${nextView}`)
                        }
                      >
                        <span className="truncate text-left">
                          {selectedOrg?.name || "Select Organization"}
                        </span>
                      </Button>

                      <TreeDropdown
                        data={treeNodesToTreeItems(treeData)}
                        onSelect={(item) => goToOrg(item.id || "")}
                        selectedItemName={selectedOrg?.name}
                        loading={isPending}
                        dependencyKey={`${memoData.length}`}
                        side="right"
                        align="start"
                        sideOffset={0}
                        currentPlanName={currentPlanName}
                        trigger={
                          <Button
                            variant="ghost"
                            className={cn(
                              "flex items-center justify-center rounded-md bg-background/40 p-2 text-sm font-medium hover:bg-background/90",
                            )}
                            title="Select Organization"
                          >
                            <TiArrowUnsorted
                              className="ml-1 size-3.5 shrink-0 text-white"
                              aria-hidden
                            />
                          </Button>
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
              <ProductSelector />
              <SharedSidebar />
              <Separator className="my-3" />
              <div className="mb-2 flex items-center gap-2 px-1">
                <p
                  className="min-w-0 flex-1 truncate text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  title={projectFilterLabel}
                >
                  {projectFilterLabel}
                </p>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    id="tour1-step2-createproject"
                    className="size-6 shrink-0 rounded-md bg-sky-500 text-white shadow-sm hover:bg-sky-600 hover:text-white"
                    title="Add project"
                    aria-label="Add project"
                    onClick={() => setIsCreateWorkFormOpen(true)}
                  >
                    <LuPlus className="size-3.5" />
                  </Button>
                  {workAccessLevels && workAccessLevels.length > 0 && (
                    <DropdownMenu>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={isWorkAccessLevelsLoading}
                              className={cn(
                                "size-8 shrink-0 rounded-md",
                                selectedAccessLevelFilter !== null
                                  ? "bg-sky-500 text-white shadow-sm hover:bg-sky-600 hover:text-white"
                                  : "text-muted-foreground",
                              )}
                              aria-label={projectFilterLabel}
                              aria-pressed={selectedAccessLevelFilter !== null}
                            >
                              <Filter className="size-4" />
                              <span className="sr-only">Filter projects</span>
                            </Button>
                          </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          {projectFilterLabel}
                        </TooltipContent>
                      </Tooltip>
                      <DropdownMenuContent
                        align="end"
                        className="min-w-44 bg-background"
                      >
                        <DropdownMenuRadioGroup
                          value={selectedAccessLevelFilter ?? "all"}
                          onValueChange={(next) => {
                            setSelectedAccessLevelFilter(
                              next === "all" ? null : next,
                            );
                          }}
                        >
                          <DropdownMenuRadioItem
                            value="all"
                            className="text-sm"
                          >
                            All Projects
                          </DropdownMenuRadioItem>
                          {workAccessLevels.map((level) => (
                            <DropdownMenuRadioItem
                              key={level.id}
                              value={level.id || ""}
                              className="text-sm"
                              disabled={!level.id}
                            >
                              {level.name} Projects
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              <ScrollArea className="flex-1 px-1">
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <p className="text-sm text-gray-500">Loading projects...</p>
                  </div>
                ) : isError ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-4">
                    <p className="text-sm text-gray-500">
                      Failed to load work items
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        fetchWorkItems();
                      }}
                    >
                      Retry
                    </Button>
                  </div>
                ) : hasAnyProjectItems ? (
                  <div className="mb-2 space-y-1">
                    {assignedProjectItems.length > 0 ? (
                      <DashboardNav
                        items={assignedProjectItems}
                        isProjectNav={true}
                        onClick={() => setIsOpen(false)}
                      />
                    ) : null}
                    {showUnlinkedSection ? (
                      <>
                        {assignedProjectItems.length > 0 ? (
                          <Separator className="my-2" />
                        ) : null}
                        <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">
                          Unlinked projects
                        </p>
                        <DashboardNav
                          items={unlinkedProjectItems}
                          isProjectNav={true}
                          onClick={() => setIsOpen(false)}
                        />
                      </>
                    ) : null}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 py-4">
                    <p className="text-center text-sm text-muted-foreground">
                      No projects yet
                    </p>
                    <Button
                      variant="default"
                      size="sm"
                      className="w-full"
                      onClick={() => {
                        setIsCreateWorkFormOpen(true);
                      }}
                    >
                      <LuPlus className="mr-2 size-4" />
                      Add a project
                    </Button>
                  </div>
                )}
              </ScrollArea>

              <SharedSidebarFooter />
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col items-center gap-1 pb-2">
              {/* Top expand control */}
              <div className="mt-1 flex w-full justify-center">
                <Button
                  onClick={() => setIsOpen(true)}
                  variant="ghost"
                  size="icon"
                  title="Expand Sidebar"
                  className="flex size-8 items-center justify-center rounded-md text-foreground hover:bg-background/90"
                >
                  <PanelRightClose className="size-4" />
                </Button>
              </div>
              {/* Collapsed menu (scrollable) */}
              <ScrollArea className="w-full flex-1">
                <div className="flex flex-col items-center gap-1 px-0">
                  {/* Team selector trigger (collapsed) */}
                  {(session.data?.user?.role === "SUPER_ADMIN" ||
                    session.data?.user?.role === "ADMIN") && (
                    <div className="flex w-full justify-center">
                      <TreeDropdown
                        data={treeNodesToTreeItems(treeData)}
                        onSelect={(item) => goToOrg(item.id || "")}
                        selectedItemName={selectedOrg?.name}
                        loading={isPending}
                        dependencyKey={`${memoData.length}`}
                        side="right"
                        align="start"
                        trigger={
                          <Button
                            variant="outline"
                            size="icon"
                            title={selectedOrg?.name || "Select Organization"}
                            className="mb-2 size-8 rounded-md border-0 bg-[#922CF8] p-0 text-white hover:bg-[#922CF8]/90"
                          >
                            <span className="text-xs font-semibold">
                              {(selectedOrg?.name?.[0] || "T").toUpperCase()}
                            </span>
                          </Button>
                        }
                      />
                    </div>
                  )}
                  <ProductSelector collapsed />
                  {/* Browse Templates */}
                  <Button
                    onClick={() =>
                      router.push(
                        `/${orgId}/project-templates?view=${nextView}`,
                      )
                    }
                    variant="ghost"
                    size="icon"
                    title="Browse Templates"
                    className={cn(
                      "mb-1 size-8 rounded-md p-1 text-foreground hover:bg-background/90",
                      {
                        "bg-accent/70": pathname?.startsWith(
                          `/${orgId}/project-templates`,
                        ),
                      },
                    )}
                  >
                    <TbTemplate className="size-4" />
                  </Button>
                  <Button
                    onClick={() => setIsOpen(true)}
                    variant="ghost"
                    size="icon"
                    title="Projects"
                    className="mb-1 size-8 rounded-md p-1 text-foreground hover:bg-background/90"
                  >
                    {/* <LuLayoutList className="size-4" /> */}

                    {/* <GrProjects className="size-4" /> */}
                    <LuChartGantt className="size-4" />
                  </Button>
                  {/* Add Project */}
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Add Project"
                    id="tour1-step2-createproject"
                    className="mb-1 size-8 rounded-md p-1 text-foreground hover:bg-background/90"
                    onClick={() => setIsCreateWorkFormOpen(true)}
                  >
                    <LuPlus className="size-4" />
                  </Button>
                  {/* Admin (role-based) */}
                  {(session.data?.user?.role === "SUPER_ADMIN" ||
                    session.data?.user?.role === "ADMIN") && (
                    <Button
                      onClick={() =>
                        router.push(`/${orgId}/admin?view=${nextView}`)
                      }
                      variant="ghost"
                      size="icon"
                      title="Admin"
                      className="mb-1 size-8 rounded-md p-1 text-foreground hover:bg-background/90"
                    >
                      <MdOutlineAdminPanelSettings className="size-4" />
                    </Button>
                  )}
                  {/* Invite (role-based) */}
                  {(session.data?.user?.role === "SUPER_ADMIN" ||
                    session.data?.user?.role === "ADMIN") && (
                    <Button
                      onClick={() => setInviteModalOpen(true)}
                      variant="ghost"
                      size="icon"
                      title="Invite"
                      className="mb-1 size-8 rounded-md p-1 text-foreground hover:bg-background/90"
                    >
                      <TbHexagonPlusFilled className="size-4" />
                    </Button>
                  )}
                </div>
              </ScrollArea>
              {/* Bottom user menu (collapsed) */}
              <div className="mb-8 mt-auto flex w-full justify-center py-2">
                <UserNav />
              </div>
            </div>
          )}
        </div>
      </div>
      {isCreateWorkFormOpen && (
        <CreateWorkForm
          orgId={orgId as string}
          open={isCreateWorkFormOpen}
          onOpenChange={setIsCreateWorkFormOpen}
          onWorkCreated={handleWorkCreated}
          preferredProductRootId={preferredProductRootId}
        />
      )}
    </div>
  );
}
